import React from 'react';

/**
 * UOM Modal Component
 * 
 * Fields:
 * - Name: textfield
 * - Symbol: textfield
 * - Default: checkbox
 * - Relation with Default: textfield (only shown when a default item exists and current item is NOT the default)
 */

// Placeholder suggestions based on UOM type
const PLACEHOLDER_MAP = {
  'uom-weight': {
    name: 'e.g., Kilogram, Gram, Milligram, Pound',
    symbol: 'e.g., kg, g, mg, lbs',
    relationDefault: 'e.g., 1000 (if 1 kg = 1000 g)'
  },
  'uom-count': {
    name: 'e.g., Piece, Dozen, Gross, Bundle',
    symbol: 'e.g., pc, dz, gr, bd',
    relationDefault: 'e.g., 12 (if 1 dozen = 12 pieces)'
  },
  'uom-dimension': {
    name: 'e.g., Meter, Centimeter, Inch, Millimeter',
    symbol: 'e.g., m, cm, in, mm',
    relationDefault: 'e.g., 100 (if 1 m = 100 cm)'
  },
  'uom-volume': {
    name: 'e.g., Liter, Milliliter, Gallon, Cubic Meter',
    symbol: 'e.g., L, mL, gal, m³',
    relationDefault: 'e.g., 1000 (if 1 L = 1000 mL)'
  },
  'uom-loadability': {
    name: 'e.g., 40ft HC container, Full truck load',
    symbol: 'e.g., 40HC, FTL',
    relationDefault: 'e.g., 67.5 (capacity in CBM — cubic metres)',
  },
};

const UOMModal = ({ show, onClose, onSave, initial = {}, defaultItem = null, uomType = 'uom-weight' }) => {
  const [name, setName] = React.useState(initial.name || '');
  const [symbol, setSymbol] = React.useState(initial.symbol || '');
  const [isDefault, setIsDefault] = React.useState(initial.is_default ?? false);
  const [relationWithDefault, setRelationWithDefault] = React.useState(initial.relation_with_default || '');

  React.useEffect(() => {
    setName(initial.name || '');
    setSymbol(initial.symbol || '');
    setIsDefault(initial.is_default ?? false);
    setRelationWithDefault(initial.relation_with_default || '');
  }, [initial]);

  if (!show) return null;

  // Get placeholders based on UOM type
  const placeholders = PLACEHOLDER_MAP[uomType] || PLACEHOLDER_MAP['uom-weight'];

  // Show "Relation with Default" field only when:
  // 1. A default item exists
  // 2. The current item is NOT the default (either editing a non-default item, or creating new while default exists)
  const showRelationField = defaultItem && !isDefault;

  const isLoadability = uomType === 'uom-loadability';

  const relationNumericOk = () => {
    if (!showRelationField || !isLoadability) return true;
    const n = parseFloat(String(relationWithDefault).trim().replace(',', '.'));
    return Number.isFinite(n) && n > 0;
  };

  const saveDisabled =
    !name.trim() ||
    !symbol.trim() ||
    (showRelationField && isLoadability && !relationNumericOk());

  return (
    <div className="modal-backdrop d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{initial.id ? 'Edit' : 'Add'} {initial.title || 'UOM'}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {/* Name field */}
              <div className="mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input 
                  className="form-control" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  placeholder={placeholders.name}
                />
              </div>

              {/* Symbol field */}
              <div className="mb-3">
                <label className="form-label">Symbol <span className="text-danger">*</span></label>
                <input 
                  className="form-control" 
                  value={symbol} 
                  onChange={(e) => setSymbol(e.target.value)} 
                  placeholder={placeholders.symbol}
                />
              </div>

              {/* Default checkbox */}
              <div className="form-check form-switch mb-3">
                <input 
                  className="form-check-input" 
                  type="checkbox" 
                  id="modalIsDefault" 
                  checked={isDefault} 
                  onChange={(e) => setIsDefault(e.target.checked)} 
                />
                <label className="form-check-label ms-2" htmlFor="modalIsDefault">Default</label>
                {isDefault && (
                  <small className="d-block text-muted mt-1">
                    This will be the default unit for this UOM type
                  </small>
                )}
              </div>

              {/* Relation with Default — for loadability this stores CBM capacity used on the tear sheet */}
              {showRelationField && (
                <div className="mb-3">
                  <label className="form-label">
                    {isLoadability ? (
                      <>
                        Capacity (CBM) <span className="text-danger">*</span>
                      </>
                    ) : (
                      <>
                        Relation with Default ({defaultItem.name} - {defaultItem.symbol})
                      </>
                    )}
                  </label>
                  <input
                    type={isLoadability ? 'number' : 'text'}
                    min={isLoadability ? '0' : undefined}
                    step={isLoadability ? 'any' : undefined}
                    className="form-control"
                    value={relationWithDefault}
                    onChange={(e) => setRelationWithDefault(e.target.value)}
                    placeholder={placeholders.relationDefault}
                    inputMode={isLoadability ? 'decimal' : undefined}
                  />
                  <small className="text-muted d-block">
                    {isLoadability ? (
                      <>
                        Total capacity in cubic metres (CBM) for this loadability option. Each option appears as a
                        row in the tear sheet Container / Loadability table; cartons and PCS use this value.
                      </>
                    ) : (
                      <>
                        Enter the conversion factor: how many {symbol || 'units'} equal 1 {defaultItem.symbol}
                      </>
                    )}
                  </small>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() =>
                  onSave({
                    ...initial,
                    name: name.trim(),
                    symbol: symbol.trim(),
                    is_default: isDefault,
                    relation_with_default: showRelationField ? String(relationWithDefault).trim() : null,
                    active: true,
                  })
                }
                disabled={saveDisabled}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UOMModal;
